<script setup lang="ts">
import { computed } from "vue";
import { nothing } from "../lib/maybe";
import { MAP_INK_COLORS } from "../lib/mapDocument";
import ZwMenu from "./ui/ZwMenu.vue";
import ZwMenuSeparator from "./ui/ZwMenuSeparator.vue";

defineProps<{ label: string }>();

const model = defineModel<string>({ required: true });

const emit = defineEmits<{
  /** Fired when the author picks a color (for undo on selection recolor). */
  adjustStart: [];
}>();

const activePreset = computed(() =>
  MAP_INK_COLORS.find(
    (swatch) => swatch.value.toLowerCase() === model.value.toLowerCase(),
  ),
);

function selectPreset(value: string): void {
  if (value.toLowerCase() === model.value.toLowerCase()) return;
  emit("adjustStart");
  model.value = value;
}

function onCustomPointerDown(): void {
  emit("adjustStart");
}

function onCustomInput(event: Event): void {
  const value = (event.target as HTMLInputElement).value;
  if (value.toLowerCase() === model.value.toLowerCase()) return;
  model.value = value;
}
</script>

<template>
  <div class="map-ink-picker">
    <span class="map-ink-label">{{ label }}</span>
    <ZwMenu align="start" :menu-label="nothing">
      <template #trigger="{ toggle, attrs, open }">
        <button
          type="button"
          class="map-ink-swatch"
          :class="{ 'map-ink-swatch-open': open }"
          :style="{ background: model }"
          :title="`${label}: ${activePreset?.label ?? model}`"
          :aria-label="`${label} color`"
          v-bind="attrs"
          @click="toggle()"
        />
      </template>
      <div class="map-ink-menu" role="presentation">
        <div class="map-ink-grid">
          <button
            v-for="swatch in MAP_INK_COLORS"
            :key="swatch.id"
            type="button"
            class="map-ink-option"
            :class="{
              'map-ink-option-active':
                swatch.value.toLowerCase() === model.toLowerCase(),
            }"
            :style="{ background: swatch.value }"
            :title="swatch.label"
            :aria-label="swatch.label"
            @click="selectPreset(swatch.value)"
          />
        </div>
        <ZwMenuSeparator />
        <label class="map-ink-custom-field">
          <span class="map-ink-custom-label">Custom</span>
          <input
            type="color"
            class="map-ink-custom-input"
            :value="model"
            @pointerdown="onCustomPointerDown"
            @input="onCustomInput"
          />
        </label>
      </div>
    </ZwMenu>
  </div>
</template>

<style>
.map-ink-picker {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}

.map-ink-label {
  font-size: 0.75rem;
  color: var(--text-muted);
}

.map-ink-swatch {
  appearance: none;
  width: 1.85rem;
  height: 1.85rem;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: 0.25rem;
  cursor: pointer;
}

.map-ink-swatch:hover {
  background-image: none;
  filter: brightness(1.05);
}

.map-ink-swatch-open {
  border-color: color-mix(in oklch, var(--comment) 55%, var(--line));
}

.map-ink-menu {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.25rem;
  min-width: 10.5rem;
}

.map-ink-grid {
  display: grid;
  grid-template-columns: repeat(5, 1.35rem);
  gap: 0.35rem;
  padding: 0.15rem;
}

.map-ink-option {
  appearance: none;
  width: 1.35rem;
  height: 1.35rem;
  padding: 0;
  border: 1px solid color-mix(in oklch, var(--line) 80%, transparent);
  border-radius: 0.2rem;
  cursor: pointer;
}

.map-ink-option-active {
  outline: 2px solid var(--comment);
  outline-offset: 1px;
}

.map-ink-custom-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.15rem 0.25rem;
  font-size: 0.75rem;
  color: var(--text-muted);
}

.map-ink-custom-input {
  width: 2rem;
  height: 1.5rem;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: 0.2rem;
  background: transparent;
  cursor: pointer;
}
</style>
